import { usePage } from "@inertiajs/react"
import { Button } from "../../components/ui/button"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table"
import { Trash2, Edit, Eye } from "lucide-react"
import DataTablePagination from "../../components/ui/data-table-pagination"
import DataTableResults from "../../components/ui/data-table-results"
import Preloader from "../../components/ui/preloader"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog"
import EditBlogPost from "../Admin/EditBlogPost"
import { useState } from "react"
import { router } from "@inertiajs/react"
import { BlogPost as BlogPostType, PaginationData } from "../../types/blog"

declare module '@inertiajs/core' {
  interface PageProps {
    blog_posts: BlogPostType[]
    blog_pagination?: PaginationData
  }
}

const BlogPostsList = () => {
  const { blog_posts, blog_pagination } = usePage().props
  const [isLoading, setIsLoading] = useState(false)
  const [editingPost, setEditingPost] = useState<BlogPostType | null>(null)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [deletingPost, setDeletingPost] = useState<BlogPostType | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const getStatusBadge = (published: boolean) => {
    return published ? (
      <span className="bg-green-100 text-green-800 px-2 py-1 rounded-md text-xs">
        Published
      </span>
    ) : (
      <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-md text-xs">
        Draft
      </span>
    )
  }

  const handlePageChange = (page: number) => {
    setIsLoading(true)
    router.get('/admin/blog_posts', { page: page }, {
      preserveState: true,
      preserveScroll: true,
      onFinish: () => setIsLoading(false)
    })
  }

  const handleEdit = (post: BlogPostType) => {
    setEditingPost(post)
    setEditDialogOpen(true)
  }

  const handleEditSuccess = () => {
    setEditDialogOpen(false)
    setEditingPost(null)
    // The controller redirects to admin_root_path which will refresh the data automatically
  }

  const handleEditCancel = () => {
    setEditDialogOpen(false)
    setEditingPost(null)
  }

  const handleDelete = (post: BlogPostType) => {
    setDeletingPost(post)
    setDeleteDialogOpen(true)
  }

  const confirmDelete = () => {
    if (deletingPost) {
      router.delete(`/admin/blog_posts/${deletingPost.id}`, {
        onSuccess: () => {
          setDeleteDialogOpen(false)
          setDeletingPost(null)
        }
      })
    }
  }

  const cancelDelete = () => {
    setDeleteDialogOpen(false)
    setDeletingPost(null)
  }

  const renderTags = (tagsString?: string) => {
    if (!tagsString) return null
    return tagsString.split(',').map((tag: string) => (
      <span
        key={tag.trim()}
        className="bg-secondary text-secondary-foreground px-2 py-1 rounded-md text-xs"
      >
        {tag.trim()}
      </span>
    ))
  }

  return (
    <div className="space-y-4">
      {blog_posts.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          No blog posts found. Add your first blog post to get started.
        </div>
      ) : (
        <>
          {blog_pagination && <DataTableResults pagination={blog_pagination} object_name="blog posts" />}
          
          <Preloader isLoading={isLoading}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Tags</TableHead>
                  <TableHead>Featured</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {blog_posts.map((post: BlogPostType) => (
                  <TableRow key={post.id}>
                    <TableCell className="font-medium max-w-xs">
                      <div>
                        <div className="font-semibold">{post.title}</div>
                        <div className="text-sm text-muted-foreground truncate">
                          {post.excerpt}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(post.published)}
                    </TableCell>
                    <TableCell>
                      {formatDate(post.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {renderTags(post.tags_string)}
                      </div>
                    </TableCell>
                    <TableCell>
                      {post.featured ? (
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-xs">
                          Featured
                        </span>
                      ) : (
                        <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded-md text-xs">
                          Not Featured
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(`/admin/blog_posts/${post.slug}`, '_blank')}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(post)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDelete(post)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Preloader>

          {blog_pagination && (
            <DataTablePagination 
              pagination={blog_pagination} 
              onPageChange={handlePageChange}
            />
          )}

          <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Edit Blog Post</DialogTitle>
                <DialogDescription>
                  Update the blog post details below. All fields marked with * are required.
                </DialogDescription>
              </DialogHeader>
              {editingPost && (
                <EditBlogPost
                  blogPost={editingPost}
                  onSuccess={handleEditSuccess}
                  onCancel={handleEditCancel}
                />
              )}
            </DialogContent>
          </Dialog>

          <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete Blog Post</DialogTitle>
                <DialogDescription>
                  Are you sure you want to delete "{deletingPost?.title}"? This action cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={cancelDelete}>
                  Cancel
                </Button>
                <Button variant="destructive" onClick={confirmDelete}>
                  Delete
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  )
}

export default BlogPostsList